import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";

import PreviewLabel from "@/app/shared/PreviewLabel";
import { setDrawerExpanded, setDrawerOpen } from "@/store/reducers/ui";
import { getVersion } from "@/utils/utility";
import {
  viAddressBook,
  viArrowRightFromBracket,
  viArrowRightToBracket,
  viArrowRightToBracketClock,
  viBel,
  viBlf,
  viBuilding,
  viCirclePerson,
  viCity,
  viClockRotateLeft,
  viFolder,
  viFolderImage,
  viGauge,
  viGear,
  viHouse,
  viList,
  viListBarChart,
  viListClock,
  viLocationPinDot,
  viMegaphone,
  viMusicPause,
  viNumpad,
  viPeople,
  viPeopleCircle,
  viPerson,
  viPersonClock,
  viPhone,
  viPhoneClock,
  viPhoneOffice,
  viProhibited,
  viRectangleP,
  viSimCard,
  viSmartphone,
  viStore,
  viTableClock,
  viToggleOn,
  viVoicemail,
  viWebhooks,
  viWebhooksClock,
} from "@sippy-platform/valkyrie";

import { Box, Drawer, List, useMediaQuery, useTheme } from "@mui/material";

import NavigationItem from "./NavigationItem";
import ParentItem from "./ParentItem";

function MenuDrawer() {
  const theme = useTheme();
  const matchesLgUp = useMediaQuery(theme.breakpoints.up("lg"));
  const devType = getVersion("devType");
  const dispatch = useDispatch();
  const { pbxId } = useParams();

  const auth = useSelector((state) => state.auth);
  const drawerExpanded = useSelector((state) => state.ui.drawerExpanded);
  const drawerOpen = useSelector((state) => state.ui.drawerOpen);

  function pbxPath(path) {
    return `pbx/${pbxId}/${path}`;
  }

  return (
    <Drawer
      variant={matchesLgUp ? "permanent" : "temporary"}
      slotProps={{
        paper: {
          elevation: 0,
          sx: [
            {
              height: "100dvh",
              width: 256,
              border: "none",
              zIndex: (theme) => theme.zIndex.drawer,
              transition: "width .2s ease-in-out",
              backgroundColor: "background.default",
              borderRight: (theme) => `solid 1px ${theme.vars.palette.divider}`,
              overflowX: "hidden",
            },
            !matchesLgUp && {
              backgroundColor: (theme) =>
                `color-mix(in srgb, ${theme.vars.palette.background.default} 75%, transparent)`,
              backdropFilter: "blur(15px) saturate(125%)",
            },
            !drawerExpanded && { width: { xs: 256, lg: 57 } },
            drawerExpanded && { width: { lg: 256 } },
          ],
        },
        backdrop: {
          style: {
            opacity: 0,
          },
        },
      }}
      onClose={() => {
        if (matchesLgUp) {
          dispatch(setDrawerExpanded(false));
        } else {
          dispatch(setDrawerOpen(false));
        }
      }}
      open={!matchesLgUp && drawerOpen}
    >
      <Box
        sx={{
          py: 2,
          px: 1.875,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: 255,
          "& > img": { display: "block", height: 32, width: "auto" },
        }}
      >
        <img src={`/${import.meta.env.SIPPY_ICON}`} alt="Sippy logo" />
        {devType && <PreviewLabel />}
      </Box>
      {pbxId ? (
        <Box sx={{ flexGrow: 1 }}>
          <List
            sx={(theme) => ({
              height: `calc(100dvh - 64px - 56px)`,
              overflowX: "hidden",
              overflowY: "auto",
              ...theme.scrollbar,
              px: 1,
              ".MuiListItem-root": { width: 1, minHeight: 40 },
              ".MuiListItemButton-root": { width: 1, minHeight: 40, py: 0, pr: 1.5, pl: 1 },
              ".MuiListItemText-primary": { fontSize: ".875rem", fontWeight: "600" },
            })}
            component="nav"
          >
            <NavigationItem title="Dashboard" icon={viGauge} link={pbxPath("dashboard")} />

            <ParentItem title="Logs & statistics" icon={viClockRotateLeft} link={pbxPath("logs")}>
              <NavigationItem title="Call log" icon={viPhoneClock} link={pbxPath("logs/calls")} />
              <NavigationItem title="Queue log" icon={viListClock} link={pbxPath("logs/queues")} />
              <NavigationItem title="Queue statistics" icon={viListBarChart} link={pbxPath("logs/queue-stats")} />
              <NavigationItem
                title="Queue member log"
                icon={viArrowRightToBracketClock}
                link={pbxPath("logs/queue-members")}
              />
              <NavigationItem title="Flex desk log" icon={viPersonClock} link={pbxPath("logs/flex-desks")} />
              {auth.accessLevel <= 3 && (
                <NavigationItem title="Webhook log" icon={viWebhooksClock} link={pbxPath("logs/webhooks")} />
              )}
            </ParentItem>

            {auth.accessLevel <= 3 && (
              <>
                <NavigationItem title="Phonebooks" icon={viAddressBook} link={pbxPath("phonebooks")} />

                <ParentItem title="Media" icon={viFolderImage} link={pbxPath("media")}>
                  <NavigationItem title="Library" icon={viFolder} link={pbxPath("media/library")} />
                  <NavigationItem title="Music on Hold" icon={viMusicPause} link={pbxPath("media/music-on-hold")} />
                </ParentItem>

                <ParentItem title="Call flow" icon={viPhone} link={pbxPath("call-flow")}>
                  <NavigationItem title="Users" icon={viPerson} link={pbxPath("call-flow/users")} />
                  <NavigationItem title="Mobile users" icon={viSmartphone} link={pbxPath("call-flow/mobile-users")} />
                  <NavigationItem title="Groups" icon={viPeople} link={pbxPath("call-flow/groups")} />
                  <NavigationItem
                    title="Timetables"
                    icon={viTableClock}
                    link={pbxPath("call-flow/timetables")}
                    alt={[pbxPath("call-flow/timetable"), pbxPath("call-flow/holidaysets")]}
                  />
                  <NavigationItem
                    title="Inbound routes"
                    icon={viArrowRightToBracket}
                    link={pbxPath("call-flow/inbound-routes")}
                  />
                  <NavigationItem title="IVRs" icon={viNumpad} link={pbxPath("call-flow/ivrs")} />
                  <NavigationItem title="Queues" icon={viList} link={pbxPath("call-flow/queues")} />
                  <NavigationItem title="Switches" icon={viToggleOn} link={pbxPath("call-flow/switches")} />
                  <NavigationItem title="Call parking" icon={viRectangleP} link={pbxPath("call-flow/call-parkings")} />
                  <NavigationItem title="Voicemails" icon={viVoicemail} link={pbxPath("call-flow/voicemails")} />
                  <NavigationItem title="Conferences" icon={viPeopleCircle} link={pbxPath("call-flow/conferences")} />
                  <NavigationItem title="Intercoms" icon={viMegaphone} link={pbxPath("call-flow/intercoms")} />
                  <NavigationItem
                    title="Custom destinations"
                    icon={viLocationPinDot}
                    link={pbxPath("call-flow/custom-destinations")}
                  />
                  <NavigationItem
                    title="Outbound rules"
                    icon={viArrowRightFromBracket}
                    link={pbxPath("call-flow/outbound-rules")}
                  />
                </ParentItem>

                <ParentItem title="Settings" icon={viGear} link={pbxPath("settings")}>
                  <NavigationItem
                    title="Line key templates"
                    icon={viBlf}
                    link={pbxPath("settings/line-key-templates")}
                  />
                  <NavigationItem
                    title="Distinctive ringtones"
                    icon={viBel}
                    link={pbxPath("settings/distinctive-ringtones")}
                  />
                  <NavigationItem title="Blocklists" icon={viProhibited} link={pbxPath("settings/blocklists")} />
                  <NavigationItem title="Webhooks" icon={viWebhooks} link={pbxPath("settings/webhooks")} />
                </ParentItem>

                <NavigationItem title="Provisioning" icon={viPhoneOffice} link={pbxPath("provisioning")} />
              </>
            )}
          </List>
          {auth.accessLevel <= 3 && (
            <List
              sx={{
                overflowX: "hidden",
                overflowY: "auto",
                p: 1,
                ".MuiListItem-root": { width: 1, minHeight: 40 },
                ".MuiListItemButton-root": { width: 1, minHeight: 40, py: 0, pr: 1.5, pl: 1 },
                ".MuiListItemText-primary": { fontSize: ".875rem", fontWeight: "600" },
              }}
              component="nav"
            >
              <NavigationItem title="My PBX" icon={viBuilding} link={pbxPath("my-pbx")} />
            </List>
          )}
        </Box>
      ) : (
        <>
          <List
            sx={(theme) => ({
              height: `calc(100dvh - 64px - 56px)`,
              overflowX: "hidden",
              overflowY: "auto",
              ...theme.scrollbar,
              px: 1,
              ".MuiListItem-root": { width: 1, minHeight: 40 },
              ".MuiListItemButton-root": { width: 1, minHeight: 40, py: 0, pr: 1.5, pl: 1 },
              ".MuiListItemText-primary": { fontSize: ".875rem", fontWeight: "600" },
            })}
            component="nav"
          >
            <NavigationItem title="Home" icon={viHouse} link="backstage/home" exact />
            {auth?.accessLevel <= 1 && <NavigationItem title="PBXes" icon={viCity} link="backstage/pbxes" />}
            {auth?.accessLevel === 0 && <NavigationItem title="Partners" icon={viStore} link="backstage/partners" />}
            {auth?.accessLevel <= 1 && (
              <>
                <NavigationItem title="Accounts" icon={viCirclePerson} link="backstage/accounts" />
                <NavigationItem title="Phone numbers" icon={viSimCard} link="backstage/phone-numbers" />
                <NavigationItem title="Devices" icon={viPhoneOffice} link="backstage/devices" />
              </>
            )}
          </List>
          {auth?.accessLevel === 1 && (
            <List
              sx={{
                overflowX: "hidden",
                overflowY: "auto",
                p: 1,
                ".MuiListItem-root": { width: 1, minHeight: 40 },
                ".MuiListItemButton-root": { width: 1, minHeight: 40, py: 0, pr: 1.5, pl: 1 },
                ".MuiListItemText-primary": { fontSize: ".875rem", fontWeight: "600" },
              }}
              component="nav"
            >
              <NavigationItem title="My Partner" icon={viStore} link="backstage/my-partner" />
            </List>
          )}
        </>
      )}
    </Drawer>
  );
}

export default MenuDrawer;
